/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import 'fontsource-roboto';
import '../app.css';
import {
  Box,
  Grid,
  createStyles,
  IconButton,
  makeStyles,
  Paper,
  Typography,
  withStyles,
  Dialog,
} from '@material-ui/core';
import type { GridColumns } from '@material-ui/data-grid';
import {
  DataGrid,
} from '@material-ui/data-grid';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import TableCellChip from '../TableCellChip';
import type { IAllGroupingsProps, IRepairGrouping, IRepairJob } from './MassReceiveInterfaces';
import GroupingActionMenu from './GroupingActionMenu';
import UpsertJobGrouping from './UpsertJobGrouping';

const StyledDataGrid = withStyles({
  root: {
    '& .MuiDataGrid-renderingZone': {
      maxHeight: 'none !important',
    },
    '& .MuiDataGrid-cell': {
      lineHeight: 'unset !important',
      maxHeight: 'none !important',
      whiteSpace: 'normal',
    },
    '& .MuiDataGrid-row': {
      maxHeight: 'none !important',
    },
  },
})(DataGrid);

const useStyles = makeStyles(() => createStyles({
  tableContainer: {
    boxShadow: 'none',
    border: 'none',
    '& .Mui-error': {
      backgroundColor: 'rgb(126,10,15,0.1)',
      color: '#750f0f',
    },
  },
  title: {
    fontWeight: 700,
    fontSize: 18,
  },
  noFocus: {
    '& .MuiDataGrid-root': {
      '& .MuiDataGrid-colCell:focus:not(.MuiDataGrid-cellEditable)': {
        outline: 'none',
      },
    },
    '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within:not(.MuiDataGrid-cellEditable)': {
      outline: 'none',
    },
    '& .Mui-error': {
      backgroundColor: 'rgb(126,10,15,0.1)',
      color: '#750f0f',
    },
  },
}));

const AllGroupings: React.FC<IAllGroupingsProps> = (
  { repairGroupings, setRepairGroupings },
) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedGroupingNumber, setSelectedGroupingNumber] = React.useState<number | undefined>(undefined);
  const [selectedGrouping, setSelectedGrouping] = React.useState<IRepairGrouping | undefined>(undefined);
  const [showEditGroupingDialog, setShowEditGroupingDialog] = React.useState<boolean>(false);

  // action menu functions
  const handleVertIconClick = (event: React.MouseEvent<HTMLButtonElement>, groupNumberParam: number) => {
    if (!anchorEl) {
      setSelectedGroupingNumber(groupNumberParam);
      setSelectedGrouping(repairGroupings.find((rg) => rg.groupNumber === groupNumberParam));
      setAnchorEl(event.currentTarget);
    }
  };

  const handleCloseMenu = () => {
    setSelectedGroupingNumber(undefined);
    setSelectedGrouping(undefined);
    setAnchorEl(null);
  };

  const handleEditGrouping = () => {
    // open dialog
    setShowEditGroupingDialog(true);
    // close menu
    setAnchorEl(null);
  };

  const handleEditGroupingDialogClose = (updatedGrouping?: IRepairGrouping) => {
    if (updatedGrouping && selectedGrouping) {
      const groupingIndex = repairGroupings.indexOf(selectedGrouping);
      // replace selected grouping
      const tempState: IRepairGrouping[] = repairGroupings;
      tempState[groupingIndex] = updatedGrouping;
      setRepairGroupings(tempState);
    }

    // close dialog
    setShowEditGroupingDialog(false);
    // unselect grouping
    setSelectedGroupingNumber(undefined);
    setSelectedGrouping(undefined);
  };

  const handleDeleteGrouping = () => {
    if (selectedGrouping) {
      const groupingIndex = repairGroupings.indexOf(selectedGrouping);
      const tempState: IRepairGrouping[] = repairGroupings;
      // remove selected grouping
      tempState.splice(groupingIndex, 1);
      setRepairGroupings(tempState);
    }

    // close menu
    setAnchorEl(null);
    // unselect grouping
    setSelectedGroupingNumber(undefined);
    setSelectedGrouping(undefined);
  };

  const displayJobAssigned = (assignedRepairJobs: IRepairJob[]) => (
    <Grid container>
      {assignedRepairJobs.map((arj) => (
        <Grid item>
          <Box mr={1} mt={0.5} mb={0.5}>
            <TableCellChip label={arj.name} backgroundColor="#D6E1EF" fontColor="#587B9F" />
          </Box>
        </Grid>
      ))}
    </Grid>

  );

  const displayVertIcon = (groupNumber: number) => (
    <IconButton
      id="mass-receive-kabob-menu"
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleVertIconClick(e, groupNumber)}
    >
      <MoreVertIcon />
    </IconButton>
  );

  const tableColumns: GridColumns = [
    {
      field: 'groupingNumber',
      headerName: 'Grouping Number',
      flex: 0.80,
      renderCell: ({ value }) => (
        <Typography tabIndex={0} variant="body2">
          {value}
        </Typography>
      ),
    },
    {
      field: 'startingBarcode',
      headerName: 'Starting Barcode',
      flex: 0.80,
      renderCell: ({ value }) => (
        <Typography tabIndex={0} variant="body2">
          {value}
        </Typography>
      ),
    },
    {
      field: 'endingBarcode',
      headerName: 'Ending Barcode',
      flex: 0.80,
      renderCell: ({ value }) => (
        <Typography tabIndex={0} variant="body2">
          {value}
        </Typography>
      ),
    },
    {
      field: 'jobsAssigned',
      headerName: 'Jobs Assigned',
      flex: 1.25,
      renderCell: ({ value }) => (
        ((Array.isArray(value))
          ? displayJobAssigned(value)
          : <></>
        )
      ),
    },
    {
      field: 'id',
      type: 'number',
      headerName: 'Actions',
      flex: 0.5,
      sortable: false,
      headerAlign: 'right',
      renderCell: ({ value }) => (
        ((typeof (value) === 'number')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];
  const tableRows = repairGroupings?.map((rg) => ({
    id: rg.groupNumber,
    groupingNumber: rg.groupNumber,
    startingBarcode: rg.startingBarcode,
    endingBarcode: rg.endingBarcode,
    jobsAssigned: rg.jobsAssigned,
  }));

  const reducer = (accumulator:number, currentValue:number) => accumulator + currentValue;
  const totalJobs = tableRows.map((repair) => repair?.jobsAssigned.length).reduce(reducer, 0);

  return (
    <Box component="span">
      <Paper>
        <Box p={2}>
          <Box display="flex">
            <Box flexGrow={1}>
              <Typography variant="h6" color="primary" className={classes.title}>{'All Groupings'}</Typography>
            </Box>
          </Box>
        </Box>
        <Box height={`${tableRows?.length ? (Math.max((totalJobs / tableRows.length), tableRows.length) * 7) : 10}rem`}>
          <StyledDataGrid
            rows={tableRows || []}
            columns={tableColumns}
            className={classes.noFocus}
            hideFooter
            disableColumnMenu
          />
        </Box>

        {anchorEl && (
          <GroupingActionMenu
            anchorEl={anchorEl}
            handleEditGrouping={handleEditGrouping}
            handleDeleteGrouping={handleDeleteGrouping}
            handleClose={handleCloseMenu}
          />
        )}

        {selectedGrouping && (
          <Dialog fullWidth open={showEditGroupingDialog} maxWidth="lg" onClose={() => handleEditGroupingDialogClose()}>
            <Box py={2}>
              <UpsertJobGrouping
                grouping={selectedGrouping}
                groupNumber={selectedGrouping.groupNumber}
                addNewRepairGrouping={handleEditGroupingDialogClose}
              />
            </Box>
          </Dialog>
        )}
      </Paper>
    </Box>
  );
};

export default AllGroupings;
